import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ShieldCheck } from 'lucide-react'
import { api, apiErrorMessage } from '../../lib/api'
import { ClientSelect } from '../../components/ClientSelect'
import { useClientSelection } from '../../stores/clientSelection'

type ReconType = 'channel_ea' | 'spv_ea' | 'facturi_emag' | 'rambursari'

interface Result {
  id: number
  status: string
  severity: 'info' | 'warn' | 'critical'
  docId: string | null
  sourceRef: string | null
  eaRef: string | null
  diffSummary: string | null
}
interface ResultsResp {
  run: { id: number; type: string; counts: Record<string, number>; startedAt: string }
  results: Result[]
}

const STATUS_LABEL: Record<string, string> = {
  ok: 'OK',
  missing_in_ea: 'Lipsă în EA',
  missing_in_source: 'Lipsă în sursă',
  value_mismatch: 'Sumă diferită',
  partner_mismatch: 'Partener diferit',
  commission_missing: 'Comision lipsă',
  invoice_missing: 'Fără factură atașată',
  invoice_mismatch: 'Număr factură diferit',
  ambiguous_match: 'Potrivire ambiguă',
  neverificabil: 'Neverificabil',
  refund_fara_storno: 'Rambursat integral fără storno',
  refund_partial_fara_storno: 'Rambursat parțial fără storno',
  refund_in_curs: 'Rambursare recentă (sub 30 de zile)',
}
const SEV_CLASS: Record<string, string> = {
  info: 'bg-b4l-green-light text-b4l-green-dark',
  warn: 'bg-b4l-amber-light text-b4l-amber-dark',
  critical: 'bg-b4l-red-light text-b4l-red-dark',
}

export function VerificarePage() {
  const clientId = useClientSelection((s) => s.selectedClientId)
  const qc = useQueryClient()
  const [runId, setRunId] = useState<number | null>(null)
  const [filter, setFilter] = useState<string>('')

  const run = useMutation({
    mutationFn: async (type: ReconType) =>
      (await api.post('/compliance/run', { clientId, type })).data as { runId: number; counts: Record<string, number> },
    onSuccess: (r) => {
      setRunId(r.runId)
      setFilter('')
      toast.success('Verificare rulată')
      void qc.invalidateQueries({ queryKey: ['recon-results', r.runId] })
    },
    onError: (err) => toast.error(apiErrorMessage(err), { duration: 8000 }),
  })

  const { data } = useQuery({
    queryKey: ['recon-results', runId, filter],
    queryFn: async () =>
      (await api.get<ResultsResp>(`/compliance/runs/${runId}/results`, { params: filter ? { status: filter } : {} })).data,
    enabled: runId != null,
  })

  const counts = data?.run.counts ?? {}
  const results = data?.results ?? []
  const nonOk = Object.entries(counts).filter(([k]) => k !== 'ok')

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-b4l-text">Verificare</h1>
        <div className="flex items-center gap-2">
          <ClientSelect />
          <button
            onClick={() => run.mutate('channel_ea')}
            disabled={clientId == null || run.isPending}
            className="flex items-center gap-1.5 rounded-md bg-b4l-green px-3 py-1.5 text-[13px] font-medium text-white hover:bg-b4l-green-dark disabled:opacity-60"
          >
            <ShieldCheck size={14} /> eMAG ↔ EA
          </button>
          <button
            onClick={() => run.mutate('spv_ea')}
            disabled={clientId == null || run.isPending}
            className="flex items-center gap-1.5 rounded-md border border-b4l-border bg-white px-3 py-1.5 text-[13px] text-b4l-text-muted hover:bg-b4l-bg-soft disabled:opacity-60"
          >
            <ShieldCheck size={14} /> SPV ↔ EA
          </button>
          <button
            onClick={() => run.mutate('facturi_emag')}
            disabled={clientId == null || run.isPending}
            title="Comenzi finalizate fără factură atașată în eMAG, și numărul facturii comparat cu documentul din ExpertAccounts"
            className="flex items-center gap-1.5 rounded-md border border-b4l-border bg-white px-3 py-1.5 text-[13px] text-b4l-text-muted hover:bg-b4l-bg-soft disabled:opacity-60"
          >
            <ShieldCheck size={14} /> Facturi atașate
          </button>
          <button
            onClick={() => run.mutate('rambursari')}
            disabled={clientId == null || run.isPending}
            title="Comenzi pe care eMAG le-a rambursat clientului (refund în desfășurătorul de plată) fără o factură storno care să acopere suma"
            className="flex items-center gap-1.5 rounded-md border border-b4l-border bg-white px-3 py-1.5 text-[13px] text-b4l-text-muted hover:bg-b4l-bg-soft disabled:opacity-60"
          >
            <ShieldCheck size={14} /> Rambursări
          </button>
        </div>
      </div>

      {runId == null && (
        <p className="text-[13px] text-b4l-text-dim">
          Alege un client și rulează o verificare. <b>eMAG ↔ EA</b> compară facturile sincronizate cu snapshot-ul EA;
          <b> SPV ↔ EA</b> compară e-Factura (când există) cu EA; <b>Rambursări</b> caută comenzile pe care eMAG
          le-a rambursat clientului fără o factură storno care să acopere suma.
        </p>
      )}

      {data && (
        <>
          <div className="mb-4 flex flex-wrap gap-2">
            <Chip label={`OK ${counts.ok ?? 0}`} active={filter === 'ok'} onClick={() => setFilter(filter === 'ok' ? '' : 'ok')} sev="info" />
            {nonOk.map(([status, n]) => (
              <Chip
                key={status}
                label={`${STATUS_LABEL[status] ?? status} ${n}`}
                active={filter === status}
                onClick={() => setFilter(filter === status ? '' : status)}
                sev="warn"
              />
            ))}
            {nonOk.length === 0 && (
              <span className="rounded-md bg-b4l-green-light px-3 py-1 text-[12px] text-b4l-green-dark">
                Fără discrepanțe — totul se potrivește ✓
              </span>
            )}
          </div>

          <div className="overflow-hidden rounded-xl border border-b4l-border bg-white">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-b4l-border text-left text-b4l-text-muted">
                  <th className="px-3 py-2 font-medium">Severitate</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Document</th>
                  <th className="px-3 py-2 font-medium">Detaliu</th>
                </tr>
              </thead>
              <tbody>
                {results.filter((r) => filter || r.status !== 'ok').slice(0, 300).map((r) => (
                  <tr key={r.id} className="border-b border-b4l-border last:border-0">
                    <td className="px-3 py-1.5">
                      <span className={`rounded px-1.5 py-0.5 text-[11px] ${SEV_CLASS[r.severity]}`}>{r.severity}</span>
                    </td>
                    <td className="px-3 py-1.5">{STATUS_LABEL[r.status] ?? r.status}</td>
                    <td className="px-3 py-1.5">{r.docId ?? '—'}</td>
                    <td className="px-3 py-1.5 text-b4l-text-muted">
                      {r.diffSummary ?? ''}
                      {r.eaRef && <span className="ml-1 text-b4l-text-dim">· {r.eaRef}</span>}
                    </td>
                  </tr>
                ))}
                {results.filter((r) => filter || r.status !== 'ok').length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-b4l-text-dim">
                      Nicio discrepanță {filter ? `(${STATUS_LABEL[filter] ?? filter})` : ''}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

function Chip({ label, active, onClick, sev }: { label: string; active: boolean; onClick: () => void; sev: string }) {
  const base = sev === 'info' ? 'bg-b4l-green-light text-b4l-green-dark' : 'bg-b4l-amber-light text-b4l-amber-dark'
  return (
    <button
      onClick={onClick}
      className={`rounded-md px-3 py-1 text-[12px] ${base} ${active ? 'ring-2 ring-b4l-blue' : ''}`}
    >
      {label}
    </button>
  )
}
